import { Paper, Stack } from '@mui/material'
import React from 'react'

import GlyphIcon from 'components/common/GlyphIcon'
import GlyphSubIcon from 'components/common/GlyphSubIcon'
import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import Icon from 'components/Icon'
import {
  getLogo,
  getPresetKeyFromURL
} from 'components/scenarios/create/status-checks/third-party-presets'
import { getStatusCheckDetails } from 'components/scenarios/utils'
import { getStateOfNodeByType } from 'components/scenarios/utils/graph-node-lifecycle'
import { isCustomStatusCheck } from 'components/status-checks/status-checks-form-utils'
import { useStatusChecksContext } from 'context/status-checks/StatusChecksContext'
import { trans } from 'i18n/translate'
import { muiTheme } from 'mui-theme'
import theme from 'theme'
import {
  ContinuousStatusCheckNode,
  SynchronousStatusCheckNode
} from 'types/api/scenarios/scenario'

const ICON_STYLES = {
  height: muiTheme.spacing(4),
  width: muiTheme.spacing(4)
}

/**
 * If the endpoint url is one of the known services, then replace the hostname with the service name
 * Otherwise return the hostname portion of the url
 *
 * @param url {string}
 * @returns {string}
 */
function parseDisplayUrl(url: string) {
  return url && url.replace('https://', '')
}

export const StatusCheckGraphNode: React.FC<{
  node: SynchronousStatusCheckNode | ContinuousStatusCheckNode
  index?: number
  overview?: boolean
  rowActions?: React.ReactNode
  isClickable?: boolean
  onClick?: (
    node: SynchronousStatusCheckNode | ContinuousStatusCheckNode
  ) => void
}> = ({
  node,
  index = 1,
  rowActions = null,
  isClickable = false,
  onClick = () => null
}) => {
  const { statusChecksRaw } = useStatusChecksContext()

  const { isNotStarted, isActive } = getStateOfNodeByType(node)

  const endpointUrl = node.endpointConfiguration?.url
  const displayUrl = parseDisplayUrl(endpointUrl)

  const nodeStatusCheck = statusChecksRaw.find(
    (statusCheck) => statusCheck.identifier === node?.statusCheckId
  )

  const isCustom = node.isAutoGenerated
    ? false
    : isCustomStatusCheck(nodeStatusCheck)

  const { color, icon } = getStatusCheckDetails(node)

  return (
    <Paper
      sx={(muiTheme) => ({
        width: 'fit-content',
        minWidth: 500,
        maxWidth: 800,
        padding: 1,
        marginY: muiTheme.spacing(2)
      })}
      onClick={isClickable ? () => onClick(node) : undefined}
    >
      <Stack
        aria-label={`${trans('status_checks.label.singular')} ${index + 1}`}
        direction="row"
        justifyContent="space-between"
      >
        <Stack alignItems="center" direction="row" spacing={1}>
          <GlyphIcon
            backgroundColor={isCustom ? theme.colors.steel[2] : 'none'}
            css={ICON_STYLES}
          >
            {node.isAutoGenerated ? (
              <Icon css={ICON_STYLES} icon="statusCheckGremlinIcon" />
            ) : isCustom ? (
              <Icon
                css={{
                  ...ICON_STYLES,
                  color: theme.colors.gray[1]
                }}
                icon="wavePulse"
              />
            ) : (
              <img
                css={ICON_STYLES}
                src={getLogo(
                  getPresetKeyFromURL(node?.endpointConfiguration?.url)
                )}
              />
            )}
            {!isNotStarted && !isActive && (
              <GlyphSubIcon
                css={{
                  background: color,
                  height: muiTheme.spacing(2),
                  width: muiTheme.spacing(2),
                  bottom: 0,
                  right: `-${muiTheme.spacing(1)}`,
                  border: `2px solid ${theme.colors.white[0]}`,
                  padding: 0
                }}
              >
                <Icon
                  css={{
                    margin: 'auto'
                  }}
                  icon={icon}
                />
              </GlyphSubIcon>
            )}
          </GlyphIcon>
          <Stack direction="column">
            <TruncatedTextWithTooltip
              data-test-status-check-name
              aria-label={`${trans('status_checks.label.singular')} ${
                node.name
              }`}
              sx={{
                fontSize: theme.fontSizes[1],
                fontWeight: theme.fontWeights[1]
              }}
              tooltipProps={{ title: node.name }}
              variant="h4"
            >
              {node.name}
            </TruncatedTextWithTooltip>
            {!isClickable && (
              <TruncatedTextWithTooltip
                data-test-status-check-url
                aria-label={`URL: ${endpointUrl}`}
                sx={{
                  maxWidth: 700 // Derived from the width set in the Paper props for this component
                }}
                tooltipProps={{ title: endpointUrl }}
                variant="caption"
              >
                {displayUrl}
              </TruncatedTextWithTooltip>
            )}
          </Stack>
        </Stack>

        {rowActions && (
          <div
            css={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignContent: 'center'
            }}
          >
            {rowActions}
          </div>
        )}
      </Stack>
    </Paper>
  )
}
